// FLAGS: authorization
// Grants the Psychologist role to an existing OpenUp employee account.
import { Dialog } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { usePsychologistMutation } from "@src/queries/experts";
import { DialogActionButtons, DialogHeader } from "./DialogToolkit";
import PsychologistsAddEmployers from "./PsychologistsAddEmployers";
import { withSnackbar } from "./SnackBarComponent";

const useStyles = makeStyles({
  form: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  formItem: {
    flexBasis: "calc(50% - 12px)",
    height: 72,
  },
});

const AddPsychologistDialog = ({ client = {}, ...props }) => {
  const { t } = useTranslation();
  const { mutateAsync: addPsychologist } = usePsychologistMutation();
  const classes = useStyles();

  const formik = useFormik({
    initialValues: {
      userId: client.id ?? "",
    },
    // FLAGS: authorization
    // Grants the Psychologist role to the selected account. The client-data endpoints gate on
    // ExpertPolicy, so this is what makes client dossiers and check-ins reachable for that user, and
    // there is no confirmation step in front of it.
    onSubmit: async (values) => {
      try {
        formik.setSubmitting(true);
        await addPsychologist(values.userId);

        props.onClose();
      } catch (e) {
        if (e.response.errors === undefined) {
          throw e;
        }

        for (const [key, value] of Object.entries(e.response.errors)) {
          props.snackbarShowMessage(`${key}: ${value}`);
        }

        formik.setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  return (
    <Dialog
      {...props}
      onClose={() => {
        formik.handleReset();
        props.onClose();
      }}
    >
      <DialogHeader
        iconLigature="person_add"
        title={t("Psychologist.Add")}
        outlined
      />

      <form onSubmit={formik.handleSubmit} className={classes.form} noValidate>
        <PsychologistsAddEmployers
          onChange={(user) => {
            formik.setFieldValue("userId", user.id);
          }}
        />
      </form>
      <DialogActionButtons
        onCancel={() => {
          formik.handleReset();
          props.onClose();
        }}
        onSubmit={formik.submitForm}
      />
    </Dialog>
  );
};

export default withSnackbar(AddPsychologistDialog);
